export function getYear(date?: Date | string): string | undefined {
  if (!date) {
    return undefined
  }
  const d = typeof date === 'string' ? new Date(date) : date
  return d.getFullYear().toString()
}

/**
 * Canonical serializer for a **calendar date** (a day, no time-of-day) to the wire
 * format `"YYYY-MM-DD"`, built from the value's **local** Y/M/D components.
 *
 * Use this — never `Date.prototype.toISOString()` — whenever a value that originates
 * from a date picker or a native `<input type="date">` is sent to the backend.
 * `.toISOString()` converts to UTC, which shifts the calendar day to the previous one
 * for any positive-UTC-offset timezone (Germany is UTC+1/+2): a picked 15.07 becomes
 * `2026-07-14T22:00:00Z`. A bare `"YYYY-MM-DD"` is parsed by the backend (Jackson) as
 * midnight UTC, so the day is preserved end-to-end.
 *
 * NB: the app-wide Material datepicker uses the moment adapter with `useUtc: true`, so a
 * *standard* picker already yields a UTC-midnight moment where `.toISOString()` happens to
 * be correct. The shift only bites native-`Date` sources — components that override with
 * `provideNativeDateAdapter()` and native `<input type="date">`. When in doubt use this
 * helper; it is correct for both.
 */
export function toInputDate(date: undefined): undefined
export function toInputDate(date: Date | string): string
export function toInputDate(date?: Date | string): string | undefined
export function toInputDate(date?: Date | string): string | undefined {
  if (!date) {
    return undefined
  }
  const d = typeof date === 'string' ? new Date(date) : date
  const month = (d.getMonth() + 1).toString().padStart(2, '0')
  const day = d.getDate().toString().padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

/**
 * Inverse of {@link toInputDate}: parses a `"YYYY-MM-DD"` calendar date into a
 * **local-midnight** `Date` (for a picker model or a local date comparison). Never
 * round-trip `fromInputDate(x).toISOString()` — that re-introduces the UTC day-shift;
 * send the `"YYYY-MM-DD"` string as-is instead.
 */
export function fromInputDate(date: undefined): undefined
export function fromInputDate(date: string): Date
export function fromInputDate(date: Date): Date
export function fromInputDate(date?: string | Date): Date | undefined
export function fromInputDate(date?: string | Date): Date | undefined {
  if (!date) {
    return undefined
  } else if (date instanceof Date) {
    return date
  }
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Vorbelegung eines `mat-datepicker` mit einem Backend-Zeitpunkt: UTC-Mitternacht des
 * **lokalen** Kalendertags.
 *
 * Der Moment-Adapter der Apps läuft auf `useUtc: true` und zeigt den UTC-Tag. Das Backend liefert
 * einen Tag aber oft als lokale Mitternacht (`2026-11-30T23:00:00Z` für den 01.12.). Roh per
 * `new Date(value)` vorbelegt, zeigt der Picker dann den Vortag, die `date`-Pipe daneben (Ortszeit)
 * den richtigen — und wer unverändert speichert, schreibt den Vortag fest (2026-11-03, Dialog
 * „Genehmigung bearbeiten": 30.11. im Feld, 01.12. in der Akte). Mit diesem Helfer zeigen Picker und
 * Pipe denselben Tag, und `toISOString()` liefert beim Speichern dieselbe UTC-Mitternacht, die auch
 * ein im Picker gewählter Tag ergibt.
 */
export function toPickerDate(value?: string | Date | null): Date | null {
  if (!value) {
    return null
  }
  const d = typeof value === 'string' ? new Date(value) : value
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
}
